import { CreatePostFormV2 } from "@/components/CreatePostFormV2";
import { PostSkeleton } from "@/components/PostSkeleton";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { useAuth } from "@/context/AuthContext";
import { usePosts } from "@/context/PostsContext";
import { usePost } from "@/hooks/usePost";
import { Post } from "@/types/post";
import { cachePost } from "@/utils/postCache";
import { router, useLocalSearchParams, useNavigation } from "expo-router";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function PostEditScreen() {
  const { postId } = useLocalSearchParams<{ postId: string }>();
  const { post, loading } = usePost(postId);
  const { userData } = useAuth();
  const { setPosts } = usePosts();
  const { t } = useTranslation();
  const navigation = useNavigation();

  const canEdit = !!userData?.capabilities?.canEditPost;

  // Server-side rules are the real check; this redirect is just UX for a
  // user who manually navigates to the edit URL without the capability.
  // Wait until userData has resolved (!= undefined-shape) before redirecting
  // so we don't bounce an admin on first paint.
  useEffect(() => {
    if (userData === null) return;
    if (!canEdit) {
      router.replace("/(tabs)");
    }
  }, [canEdit, userData]);

  const handleSaved = (updated: Post) => {
    cachePost(updated);
    setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

    // CustomHeader's back button uses React Navigation's own stack
    // (navigation.goBack()), which is independent of browser history
    // and does not collapse through expo-router's router.replace on
    // web. Pop the edit screen off the RN stack first, then push the
    // detail route — the detail screen ends up on top of whatever was
    // below edit (feed, or the previous detail entry), so the header
    // back button skips edit entirely.
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
    router.push({
      pathname: "/(tabs)/post/[postId]",
      params: { postId: updated.id },
    });
  };

  if (loading) {
    return (
      <ThemedView style={{ flex: 1 }}>
        <PostSkeleton />
      </ThemedView>
    );
  }

  if (!post) {
    return (
      <ThemedView
        style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
      >
        <ThemedText>{t("POST_NOT_FOUND")}</ThemedText>
      </ThemedView>
    );
  }

  return (
    <CreatePostFormV2
      mode="edit"
      initialPost={post}
      onSaved={handleSaved}
    />
  );
}
